import * as _ from "lodash";
import {RRule} from "rrule";
import {IBusinessObject} from "../PA.Core/PA.Core.IF.BO";
import {IMessageProvider, IProgressProvider} from "../PA.Core/PA.Core.IF.Basic";
import {PAdbContext} from "../PA.Core/PAdbContext";
import {IdentityProvider} from "../PA.Core/Basic/Identity";
import {MessageProvider} from "../PA.Core/Basic/Messages";
import {ProgressProvider} from "../PA.Core/Basic/Progress";
import {
  WB_JobProcessRow,
  WB_JobRow,
  WB_JobScheduleJobRow,
  WB_JobScheduleProcessRow,
  WB_JobScheduleRow
} from "../PA.Core/PAdbSchemaRows";
import {IwbJobSchedule} from "../PA.Core/PAdbSchemaRowsIF";
import {
  esWB_Job,
  esWB_Job_Process,
  esWB_Job_Schedule,
  esWB_Job_Schedule_Job,
  esWB_Job_Schedule_Process
} from "../PA.Core/PAdbSchema";
import {ESRecord} from "../PA.Core/lib/ESRecord";
import {ESDataStore} from "../PA.Core/lib/ESDataStore";
import {CreateOptions, ESRecordResolver} from "../PA.Core/lib/ESRecordResolver";
import {ASync} from "../PA.Core/Basic/ASync";
import {TextUtils} from "../PA.Core/Basic/Text";
import {JSONUtils} from "../PA.Core/Basic/JSON";
import {DateUtils} from "../PA.Core/Basic/Dates";
import {PAEnvironment} from "../PA.Core/PAEnvironment";

export class JobSchedulerBO implements IBusinessObject {
  Identity: IdentityProvider;

  CreateOptions: any;

  LoadOptions: any;
  _bIsLoaded: boolean;
  _OnLoadComplete: Promise<IBusinessObject>;
  LoadProgressProvider: IProgressProvider;

  ValidateOptions: any;
  _bIsValidated: boolean;
  ValidateMessageProvider: IMessageProvider;
  ValidateProgressProvider: IProgressProvider;

  SaveOptions: any;
  _bIsSaved: boolean;
  SaveProgressProvider: IProgressProvider;

  DeleteOptions: any;
  _bIsDeleted: boolean;
  DeleteMessageProvider: IMessageProvider;
  DeleteProgressProvider: IProgressProvider;

  dbContext: PAdbContext;
  drWB_JobSchedule: WB_JobScheduleRow;

  constructor(options?: any) {
    this.CreateOptions = options || {};

    if ("dbContext" in this.CreateOptions)
      this.dbContext = this.CreateOptions.dbContext;

    this.Identity = new IdentityProvider({ id: 0, name: "New Job Schedule" });

    this.LoadOptions = {};
    this._bIsLoaded = false;
    this._OnLoadComplete = null;
    this.LoadProgressProvider = new ProgressProvider("Loading", this);

    this.ValidateOptions = {};
    this._bIsValidated = false;
    this.ValidateMessageProvider = new MessageProvider();
    this.ValidateProgressProvider = new ProgressProvider("Validating", this);

    this.SaveOptions = {};
    this._bIsSaved = false;
    this.SaveProgressProvider = new ProgressProvider("Saving", this);

    this.DeleteOptions = {};
    this._bIsDeleted = false;
    this.DeleteMessageProvider = new MessageProvider();
    this.DeleteProgressProvider = new ProgressProvider("Deleting", this);
    this.drWB_JobSchedule = null;
  }

  get WB_JobSchedule(): WB_JobScheduleRow {
    return this.drWB_JobSchedule;
  }

  //<editor-fold desc = "IBusinessObject operations">

  LoadNew(options?: object): Promise<IBusinessObject> {
    var self = this;
    this.LoadProgressProvider.start("Creating Job Schedule");
    this._OnLoadComplete = new Promise<IBusinessObject>((resolve, error) => {
      self.LoadOptions = options || {};
      if ("dbContext" in self.LoadOptions)
        self.dbContext = self.LoadOptions.dbContext;
      else self.dbContext = new PAdbContext();

      if (this.dbContext == null) {
        var sError = "Unable to determine dbContext for new Job Schedule.";
        self._OnLoadComplete = null;
        self.LoadProgressProvider.exception(sError);
        error(sError);
        return;
      }

      // Create record using dbContext
      this.dbContext
          .createRecordWithKey(esWB_Job_Schedule)
          .then((newJobScheduleRow: WB_JobScheduleRow) => {
            newJobScheduleRow.setDefaults();
            self.drWB_JobSchedule = newJobScheduleRow;
            //add this to all the BOs
            self.dbContext.add(esWB_Job_Schedule, newJobScheduleRow);

            self.Identity.setAll(
                self.WB_JobSchedule.cif.WB_JOBSCHEDULE_ID,
                self.WB_JobSchedule.cif.SCHEDULE_ID,
                self.WB_JobSchedule.cif.SCHEDULE_NAME
            );
            self.LoadProgressProvider.setName(
                "Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")"
            );
            self.Identity.listenToRecordUpdates(
                self.WB_JobSchedule,
                "WB_JOBSCHEDULE_ID",
                "SCHEDULE_ID",
                "SCHEDULE_NAME"
            );

            self.LoadProgressProvider.finish();
            self._bIsLoaded = true;
            self._OnLoadComplete = null;
            resolve(self);
          })
          .catch(err => {
            self.LoadProgressProvider.exception(err);
            error(err);
          });
    });

    return this._OnLoadComplete;
  }

  Load(anId: number | number, options?: object): Promise<IBusinessObject> {
    var self = this;

    let recordResolver: ESRecordResolver;
    let recordResolverLoadPromise: Promise<void>;

    if (!this.bIsLoaded)
      this.Identity.setAll(anId, anId, "Loading Job scheduler (" + anId + ")");

    this._OnLoadComplete = new Promise<IBusinessObject>((resolve, reject) => {
      self.LoadProgressProvider.start("Loading (" + self.Identity.id_ui + ")");

      if (self.bIsLoaded) {
        var sError = "Job Scheduler already loaded.";
        self._OnLoadComplete = null;
        self.LoadProgressProvider.exception(sError);
        reject(sError);
        return;
      }

      self.LoadOptions = options || {};
      if ("dbContext" in self.LoadOptions)
        self.dbContext = self.LoadOptions.dbContext;
      else self.dbContext = new PAdbContext();

      if (this.dbContext == null) {
        var sError = "Unable to determine dbContext for new Job Scheduler.";
        self._OnLoadComplete = null;
        self.LoadProgressProvider.exception(sError);
        reject(sError);
        return;
      }
      //Added for record resolver
      if ("RecordResolver" in self.LoadOptions) {
        recordResolver = (<any>self.LoadOptions).RecordResolver;
        recordResolverLoadPromise = ASync.resolve();
      } else {
        recordResolver = JobSchedulerBO.createRecordResolver({
          dbContext: self.dbContext
        });
        recordResolverLoadPromise = JobSchedulerBO.setupRecordResolver(
            recordResolver,
            [anId]
        );
      }

      recordResolverLoadPromise
          .then(function() {
            let aJobScheulerRow: any = recordResolver.get(
                esWB_Job_Schedule,
                anId
            );

            if (_.isEmpty(aJobScheulerRow)) {
              reject("JobSchedule not Found");
              return;
            }

            self.drWB_JobSchedule = aJobScheulerRow;
            self.Identity.setAll(
                self.WB_JobSchedule.cif.WB_JOBSCHEDULE_ID,
                self.WB_JobSchedule.cif.SCHEDULE_ID,
                self.WB_JobSchedule.cif.SCHEDULE_NAME
            );
            self.LoadProgressProvider.setName(
                "Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")"
            );
            self.Identity.listenToRecordUpdates(
                self.WB_JobSchedule,
                "WB_JOBSCHEDULE_ID",
                "SCHEDULE_ID",
                "SCHEDULE_NAME"
            );
            self._bIsLoaded = true;
            self._OnLoadComplete = undefined;
            self.LoadProgressProvider.finish();
            resolve(self);
          })
          .catch(function(err: any) {
            self._OnLoadComplete = undefined;
            self.LoadProgressProvider.exception(err);
            reject(err);
            return;
          });
    });

    return this._OnLoadComplete;
  }


  get OnLoadComplete(): Promise<IBusinessObject> {
    return this._OnLoadComplete;
  }

  get bIsLoaded(): boolean {
    return this._bIsLoaded;
  }

  Validate(options?: any): Promise<IBusinessObject> {
    var self = this;
    this.ValidateMessageProvider.clearMessages();
    this.ValidateProgressProvider.start(
        "Validating " +
        self.WB_JobSchedule.cif.SCHEDULE_NAME +
        "(" +
        self.WB_JobSchedule.cif.SCHEDULE_ID +
        ")"
    );
    return new Promise<IBusinessObject>((resolve, reject) => {
      // Does no validations yet
      self.ValidateOptions = options || {};

      if (self.dbContext == null) {
        var sError = "Unable to determine dbContext for to Validate Job.";
        self.ValidateProgressProvider.exception(sError);
        reject(sError);
      }

      self._bIsValidated = true;
      self.ValidateProgressProvider.finish();
      resolve(self);
    });
  }

  get bIsValidated(): boolean {
    return this._bIsValidated;
  }

  Save(options?: any): Promise<IBusinessObject> {
    return this.SaveUsing(false, options);
  }

  SaveBatch(options?: any): Promise<IBusinessObject> {
    return this.SaveUsing(true, options);
  }

  private SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {
    var self = this;
    var wbJobSchedule = <IwbJobSchedule>self.drWB_JobSchedule;
    this.SaveProgressProvider.start(
        "Saving " +
        wbJobSchedule.SCHEDULE_NAME +
        "(" +
        wbJobSchedule.SCHEDULE_ID +
        ")"
    );

    return new Promise<IBusinessObject>((resolve, reject) => {
      // Doesn't need to do anything yet
      // All the records are in the store, since they were added through find or createNewRecord.
      // -- We could search the tree if the there is any record where store isn't set.

      if (this.dbContext == null) {
        self._bIsSaved = false;
        var sError = "Unable to determine dbContext to Save Meta.";
        self.SaveProgressProvider.exception(sError);
        reject(sError);
        return;
      }
      self.SaveOptions = options || {};

      var saveFunction: (
          esName: string,
          saveRecords: ESRecord[] | ESRecord,
          options?: any
      ) => Promise<ESDataStore>;
      if (bBatch) {
        saveFunction = self.dbContext.SaveBatch.bind(self.dbContext);
      } else {
        saveFunction = self.dbContext.Save.bind(self.dbContext);
      }

      saveFunction(esWB_Job_Schedule, self.drWB_JobSchedule, {})
          .then(val => {
            return saveFunction(
                esWB_Job_Schedule_Job,
                self.drWB_JobSchedule.WB_JobScheduleJobRows
            );
          })
          .then(ignored => {
            if (bBatch) {
              self.dbContext.addBatchCompleteListener(() => {
                self._bIsSaved = true;
              });
            } else {
              self._bIsSaved = true;
            }
            self.SaveProgressProvider.finish();
            resolve(self);
          })
          .catch(err => {
            self.SaveProgressProvider.exception(err);
            resolve(self);
          });
    });
  }

  get bIsSaved(): boolean {
    return this._bIsSaved;
  }

  Delete(options?: any): Promise<IBusinessObject> {
    return this.DeleteUsing(true, options);
  }

  DeleteOnSave(options?: any): Promise<IBusinessObject> {
    return this.DeleteUsing(false, options);
  }

  private DeleteUsing(
      bSaveNow: boolean,
      options?: any
  ): Promise<IBusinessObject> {
    var self = this;
    var wbJob = <IwbJobSchedule>self.drWB_JobSchedule;
    this.DeleteMessageProvider.clearMessages();
    this.DeleteProgressProvider.start(
        "Deleting " + wbJob.JOB_NAME + "(" + wbJob.WB_JOB_ID + ")"
    );
    return new Promise<IBusinessObject>((resolve, error) => {
      // 1. Validate if we can delete the Meta

      // 2. If Valid, mark all the records for deletion.
      self.drWB_JobSchedule.markDeleted();

      if (bSaveNow) {
        self
            .SaveUsing(false)
            .then(ignore => {
              self._bIsDeleted = true;
              self.DeleteProgressProvider.finish();
              resolve(self);
            })
            .catch(err => {
              this.DeleteProgressProvider.exception(err);
              resolve(self);
            });
      } else {
        self._bIsDeleted = true;
        self.DeleteProgressProvider.finish();
        resolve(self);
      }
    });
  }

  get bIsDeleted(): boolean {
    return this._bIsDeleted;
  }

  //</editor-fold>


  //<editor-fold desc="Add/ Remove Job API">

  addJob(aJobId: number): Promise<IBusinessObject> {
    const self = this;

    return new Promise<IBusinessObject>((resolve, error) => {
      let aJobRow: WB_JobRow = undefined;
      self.dbContext.find(esWB_Job, aJobId)
          .then((pJobRow: WB_JobRow) => {
            if (!pJobRow) {
              return;
            }
            aJobRow = pJobRow;
            return self.dbContext.createRecordWithKey(esWB_Job_Schedule_Job);
          })
          .then((aNewRow: WB_JobScheduleJobRow) => {
            if (!aJobRow) {
              resolve(self);
              return;
            }
            aNewRow.setDefaults();
            aNewRow.cif.WB_JOB_ID = aJobRow.cif.WB_JOB_ID;
            aNewRow.WB_JobRow = aJobRow;
            aNewRow.cif.WB_JOBSCHEDULE_ID =
                self.WB_JobSchedule.cif.WB_JOBSCHEDULE_ID;
            self.dbContext.add(esWB_Job_Schedule_Job, aNewRow);
            resolve(self);
          })
          .catch(err => {
            error(err);
          });
    });
  }

  //</editor-fold>
//<editor-fold desc="Scheduler Process required API">
  removeJobs (aJob_Id: number): Promise<JobSchedulerBO> {
    var self = this

    return new Promise<JobSchedulerBO>((resolve, error) => {

      _.forEach(self.drWB_JobSchedule.WB_JobScheduleJobRows, (aJobScheduleJobRow: WB_JobScheduleJobRow) => {
        if (aJobScheduleJobRow.isActive && aJobScheduleJobRow.cif.WB_JOB_ID == aJob_Id) {
          aJobScheduleJobRow.markDeleted();
        }
      });
      resolve(self)
    });
  }
  //</editor-fold>

  //<editor-fold desc="Functions to support UI schedule preview">

  get ScheduleDateTimes(): Date[] {
    let rule = this.getRRule();
    try {
      return rule.all();
    } catch (e) {
      console.error(e);
      return [];
    }
  }

  get ScheduleDateText(): string {
    // TextArea Binding {BO>/ScheduleDateText}
    return this.ScheduleDateTimes.join("\n");
  }
  //</editor-fold>





  //<editor-fold desc="Scheduler Process required API">

  async Start(options?: {RunStamp?: string, [id:string]: any}): Promise<void> {

    const self = this;
    let opts = options || {};
    let query: any = {
      where: {
        WB_JOBSCHEDULE_ID: self.WB_JobSchedule.cif.WB_JOBSCHEDULE_ID,
        DELETED_IND: {neq: '1'},
        PROCESS_STATUS: "Running"
      }
    };

    //if the job schedule can only have one running at a time, check to make sure no others are running before scheduling
    let runningJobScheduleProcess = <WB_JobScheduleProcessRow []>await self.dbContext.findAll(esWB_Job_Schedule_Process, query);

    if(!_.isEmpty(runningJobScheduleProcess) && self.WB_JobSchedule.cif.CONCURRENCY_POLICY === 'Single') {
      console.log(`${(new Date()).toUTCString()}: Process is already running for job schedule ${self.WB_JobSchedule.cif.SCHEDULE_ID}: ${self.WB_JobSchedule.cif.SCHEDULE_NAME}`);
      return;
    }

    let aNewSPRow = <WB_JobScheduleProcessRow> await self.dbContext.createRecordWithKey(esWB_Job_Schedule_Process);

    if ('RunStamp' in opts) {
      aNewSPRow.cif.RUN_STAMP = opts.RunStamp;
    } else {
      aNewSPRow.cif.RUN_STAMP = "OnDemand:" + new Date().toString();
    }

    aNewSPRow.setDefaults();
    aNewSPRow.cif.WB_JOBSCHEDULE_ID = self.WB_JobSchedule.cif.WB_JOBSCHEDULE_ID;
    aNewSPRow.cif.PROCESS_STATUS = "Running";
    aNewSPRow.cif.PROCESS_START_DTTS = DateUtils.convertToSAPBWTs(new Date());
    aNewSPRow.cif.PROCESS_END_DTTS = 0;
    aNewSPRow.cif.PROCESS_ERROR_DTTS = 0;
    aNewSPRow.cif.ERROR_IND = "0";
    aNewSPRow.cif.ERROR_MSG = "";
    self.dbContext.add(esWB_Job_Schedule_Process, aNewSPRow);

    let runOrder = 0;
    let runOrderPrior = 982941;

    let arrNewJobProcessRows: WB_JobProcessRow[] = [];

    let scheduleJobRelRows = _.orderBy(self.WB_JobSchedule.WB_JobScheduleJobRows,"RUN_ORDER");
    for (let aRelRecord of scheduleJobRelRows)
    {
      if (!aRelRecord.isActive) {continue;}

      if (aRelRecord.cif.RUN_ORDER != runOrderPrior) {
        runOrder += 1;
        runOrderPrior = aRelRecord.cif.RUN_ORDER;
      }

      // Create Job Process Row
      let aJPRow: WB_JobProcessRow = <WB_JobProcessRow> await self.dbContext.createRecordWithKey(esWB_Job_Process);
      aJPRow.setDefaults();
      aJPRow.cif.PROCESS_ID = TextUtils.padStart(aJPRow.cif.WB_JOBPROCESS_ID.toString(36), 12,"0").toUpperCase();
      aJPRow.cif.WB_JOB_ID = aRelRecord.cif.WB_JOB_ID;
      aJPRow.cif.WB_JOBSCHEDULEPROCESS_ID = aNewSPRow.cif.WB_JOBSCHEDULEPROCESS_ID;
      let scheduleJobOptions = JSONUtils.parse(aRelRecord.cif.JOB_PROCESS_OPTIONS,{});
      //
      let jobOptions = {};
      if(aRelRecord.WB_JobRow) {
        jobOptions = JSONUtils.parse(aRelRecord.WB_JobRow.cif.JOB_OPTIONS,{});
      }
      let processOptions = {};
      _.defaults(processOptions, scheduleJobOptions, jobOptions);
      aJPRow.cif.PROCESS_OPTIONS = JSON.stringify(processOptions);
      aJPRow.cif.JOBSCHEDULE_RUN_ORDER = runOrder;
      aJPRow.cif.WB_JOBSCHEDULE_ID = self.WB_JobSchedule.cif.WB_JOBSCHEDULE_ID;
      aJPRow.cif.PROCESS_STATUS = "Pending";
      aJPRow.cif.PROCESS_START_DTTS = 0;
      aJPRow.cif.PROCESS_END_DTTS = 0;
      aJPRow.cif.PROCESS_ERROR_DTTS = 0;
      aJPRow.cif.ERROR_IND = "0";
      aJPRow.cif.ERROR_MSG = "";
      aJPRow.cif.WB_JOBPROCESSOR_ID = 0;
      aJPRow.cif.JOBPROCESSOR_HB_DTTS = 0;
      await self.dbContext.add(esWB_Job_Process, aJPRow);
      arrNewJobProcessRows.push(aJPRow);
    } // Next Schedule Job R

    // Save Records in one transaction
    self.dbContext.BatchStart()
    await self.dbContext.SaveBatch(esWB_Job_Schedule_Process, aNewSPRow)
    await self.dbContext.SaveBatch(esWB_Job_Process, arrNewJobProcessRows)
    await self.dbContext.BatchComplete();

  }

  ExecutionDates_between(pollingStartDate: Date, pollingEndDate: Date): Date[] {
    try {
      let rule = this.getRRule();
      return rule.between(pollingStartDate, pollingEndDate);
    } catch (e) {
      console.error(e);
      return [];
    }
  }

  private getRRule(): RRule {
    let ruleOptions: any = {};
    if(_.isEmpty(this.WB_JobSchedule.cif.RULE_INTERVAL)) {
      ruleOptions.interval = 1;
    } else {
      let interval =this.WB_JobSchedule.cif.RULE_INTERVAL.toString()
      ruleOptions.interval = parseInt(interval);
    }

    ruleOptions.dtstart = this.WB_JobSchedule.ScheduleStartDtTs;
    ruleOptions.until = this.WB_JobSchedule.ScheduleEndDtTs;
    ruleOptions.wkst = parseInt(this.WB_JobSchedule.cif.RULE_WEEK_START_DAY);

    switch (this.WB_JobSchedule.cif.RULE_FREQUENCY) {
      case "yearly":
        ruleOptions.freq = RRule.YEARLY;
        break;
      case "monthly":
        ruleOptions.freq = RRule.MONTHLY;
        break;
      case "weekly":
        ruleOptions.freq = RRule.WEEKLY;
        break;
      case "daily":
        ruleOptions.freq = RRule.DAILY;
        break;
      case "hourly":
        ruleOptions.freq = RRule.HOURLY;
        break;
    }

    if (!_.isEmpty(this.WB_JobSchedule.cif.RULE_TIMEZONE)) {
      ruleOptions.tzid = this.WB_JobSchedule.cif.RULE_TIMEZONE;
    }

    if (this.WB_JobSchedule.cif.RULE_COUNT > 0) {
      ruleOptions.count = this.WB_JobSchedule.cif.RULE_COUNT;
    }
    if (!_.isEmpty(this.WB_JobSchedule.cif.RULE_SET_POSITIONS)) {
      ruleOptions.bysetpos = _.map(this.WB_JobSchedule.cif.RULE_SET_POSITIONS.split(","), x => parseInt(x));
    }

    if (!_.isEmpty(this.WB_JobSchedule.cif.RULE_MONTHS)) {
      ruleOptions.bymonth = _.map(this.WB_JobSchedule.cif.RULE_MONTHS.split(","), x => parseInt(x));
    }

    if (!_.isEmpty(this.WB_JobSchedule.cif.RULE_MONTH_DAYS)) {
      ruleOptions.bymonthday = _.map(this.WB_JobSchedule.cif.RULE_MONTH_DAYS.split(","), x => parseInt(x));
    }

    if (!_.isEmpty(this.WB_JobSchedule.cif.RULE_YEAR_DAYS)) {
      ruleOptions.byyearday = _.map(this.WB_JobSchedule.cif.RULE_YEAR_DAYS.split(","), x => parseInt(x));
    }

    if (!_.isEmpty(this.WB_JobSchedule.cif.RULE_WEEK_NUMBERS)) {
      ruleOptions.byweekno = _.map(this.WB_JobSchedule.cif.RULE_WEEK_NUMBERS.split(","), x => parseInt(x));
    }

    if (!_.isEmpty(this.WB_JobSchedule.cif.RULE_WEEK_DAYS)) {
      ruleOptions.byweekday = _.map(this.WB_JobSchedule.cif.RULE_WEEK_DAYS.split(","), x => parseInt(x));
    }

    if (!_.isEmpty(this.WB_JobSchedule.cif.RULE_BY_HOURS)) {
      ruleOptions.byhour = _.map(this.WB_JobSchedule.cif.RULE_BY_HOURS.split(","), x => parseInt(x));
    }

    if (!_.isEmpty(this.WB_JobSchedule.cif.RULE_BY_MINUTES)) {
      ruleOptions.byminute = _.map(this.WB_JobSchedule.cif.RULE_BY_MINUTES.split(","), x => parseInt(x));
    }

    if (!_.isEmpty(this.WB_JobSchedule.cif.RULE_BY_SECONDS)) {
      ruleOptions.bysecond = _.map(this.WB_JobSchedule.cif.RULE_BY_SECONDS.split(","), x => parseInt(x));
    }

    return new RRule(ruleOptions);
  }

  //</editor-fold>


  //<editor-fold desc="IHaveRecordResolver -- static">

  static createRecordResolver(boLoadOptions: any): ESRecordResolver {
    let opts = boLoadOptions || {};
    let dbContext: PAdbContext;

    if ("dbContext" in opts) {
      dbContext = opts.dbContext;
    } else {
      dbContext = new PAdbContext({});
    }
    const schedule_options: CreateOptions = {
      standardConditions: {},
      dctIncludeBelongsToNamedRelations: {}
    };

    const aRecResolver = new ESRecordResolver(dbContext, schedule_options);

    return aRecResolver;
  }

  static setupRecordResolver(recordResolver: ESRecordResolver, ids: string[] | number[]): Promise<void> {
    return new Promise<void>((resolve, error) => {
      recordResolver
          .findAllWithRelationships(esWB_Job_Schedule, ids)
          .then(result => {
            resolve();
          })
          .catch(err => {
            error(err);
          });
    });
  }

  //</editor-fold>

}
PAEnvironment.registerBusinessObjectClass( JobSchedulerBO , "JobSchedulerBO", "Job Scheduler")